import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/axios';
import Layout from '../components/Layout';
import PaymentLinkModal from '../components/PaymentLinkModal';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { normalizePricingTier, PRICING_TIER_OPTIONS, pricingTierLabel, resolveTierPrice } from '../utils/pricing';
import {
  cacheInventory, cacheCustomers,
  getOfflineInventory, getOfflineCustomers,
  queueSale, updateOfflineItemQty,
} from '../services/db';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) => `GHS ${parseFloat(n || 0).toFixed(2)}`;

const CATEGORY_META = {
  'Electronics':   { icon: '🔌', color: '#3B82F6', bg: '#EFF6FF' },
  'Groceries':     { icon: '🥗', color: '#22C55E', bg: '#F0FDF4' },
  'Clothing':      { icon: '👕', color: '#EC4899', bg: '#FDF2F8' },
  'Home & Kitchen':{ icon: '🏠', color: '#F59E0B', bg: '#FFFBEB' },
  'Stationery':    { icon: '📝', color: '#8B5CF6', bg: '#F5F3FF' },
  'Beverages':     { icon: '☕', color: '#06B6D4', bg: '#ECFEFF' },
  'Household':     { icon: '🧹', color: '#64748B', bg: '#F8FAFC' },
  'Pharmacy':      { icon: '💊', color: '#EF4444', bg: '#FEF2F2' },
  'Fashion':       { icon: '👗', color: '#D946EF', bg: '#FDF4FF' },
};
const catMeta = (c) => CATEGORY_META[c] || { icon: '📦', color: '#6366F1', bg: '#EEF2FF' };

const REFERRAL_SOURCES = [
  { id: 'walk_in',       label: 'Walk-in',      icon: '🚶' },
  { id: 'returning',     label: 'Returning',    icon: '🔄' },
  { id: 'whatsapp',      label: 'WhatsApp',     icon: '💬' },
  { id: 'instagram',     label: 'Instagram',    icon: '📸' },
  { id: 'tiktok',        label: 'TikTok',       icon: '🎵' },
  { id: 'facebook',      label: 'Facebook',     icon: '👍' },
  { id: 'jiji',          label: 'Jiji',         icon: '🛒' },
  { id: 'google',        label: 'Google',       icon: '🔍' },
  { id: 'friend_family', label: 'Friend/Family',icon: '👥' },
  { id: 'flyer',         label: 'Flyer/Poster', icon: '📄' },
  { id: 'other',         label: 'Other',        icon: '✨' },
];

const PAYMENT_METHODS = [
  { id: 'cash',   label: 'Cash',   icon: '💵' },
  { id: 'momo',   label: 'MoMo',   icon: '📱' },
  { id: 'card',   label: 'Card',   icon: '💳' },
  { id: 'credit', label: 'Credit', icon: '📋' },
];

const EMPTY_CUSTOMER_FORM = {
  name: '',
  phone: '',
  email: '',
  pricingTier: 'retail',
};

const EMPTY_CREDIT_FORM = {
  dueDate: '',
  notes: '',
};

const EMPTY_DISCOUNT_FORM = {
  type: 'amount',
  value: '',
  reason: '',
};

const productGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
  gap: 12,
};

const parseMoney = (value) => {
  const numeric = Number.parseFloat(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const getCustomerPricingTier = (customer) => normalizePricingTier(customer?.pricingTier);

const getUnitActivePrice = (product, unit, pricingTier = 'retail') => resolveTierPrice({
  retailPrice: unit?.sellingPrice ?? product?.price ?? 0,
  wholesalePrice: unit?.wholesalePrice,
  vipPrice: unit?.vipPrice,
  pricingTier,
});

const getSellableUnits = (product) => {
  const units = Array.isArray(product?.units) ? product.units.filter((unit) => unit.allowSale !== false) : [];
  if (units.length) return units;
  return [{
    id: null,
    name: product?.defaultSaleUnitName || product?.baseUnitName || 'Unit',
    conversionQty: 1,
    sellingPrice: Number(product?.price || 0),
    costPrice: Number(product?.cost || 0),
    isBaseUnit: true,
    isDefaultSaleUnit: true,
  }];
};

const getDefaultSaleUnit = (product) => {
  const sellableUnits = getSellableUnits(product);
  return sellableUnits.find((unit) => unit.isDefaultSaleUnit) || sellableUnits[0];
};

const getAvailableUnitCount = (product, unit) => {
  const conversionQty = Math.max(1, Number(unit?.conversionQty || 1));
  return Math.floor((Number(product?.quantity || 0)) / conversionQty);
};

const getReservedBaseQty = (cartItems, itemId, excludeCartKey = null) => (
  cartItems.reduce((sum, item) => {
    if (item.id !== itemId) return sum;
    if (excludeCartKey && item.cartKey === excludeCartKey) return sum;
    return sum + ((Number(item.baseQuantityPerUnit || 1) || 1) * item.qty);
  }, 0)
);

const buildCartItem = (product, selectedUnit, pricingTier = 'retail') => {
  const unit = selectedUnit || getDefaultSaleUnit(product);
  return {
    ...product,
    cartKey: `${product.id}:${unit.id || unit.name}`,
    unitId: unit.id || null,
    unitName: unit.name || product.defaultSaleUnitName || product.baseUnitName || 'Unit',
    baseQuantityPerUnit: Math.max(1, Number(unit.conversionQty || 1)),
    price: Number(getUnitActivePrice(product, unit, pricingTier)),
    unitCost: Number(unit.costPrice ?? product.cost ?? 0),
    availableUnits: getAvailableUnitCount(product, unit),
    qty: 1,
  };
};

// ── Skeleton product card ─────────────────────────────────────────────────────
function SkeletonProduct() {
  return (
    <div style={{ background: '#1E2235', borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ height: 100, animation: 'shimmer 1.5s infinite', backgroundSize: '200% 100%', backgroundImage: 'linear-gradient(90deg,#252A3D 25%,#2D3350 50%,#252A3D 75%)' }} />
      <div style={{ padding: 10 }}>
        <div style={{ height: 11, width: '80%', borderRadius: 6, marginBottom: 6, animation: 'shimmer 1.5s infinite', backgroundSize: '200% 100%', backgroundImage: 'linear-gradient(90deg,#252A3D 25%,#2D3350 50%,#252A3D 75%)' }} />
        <div style={{ height: 14, width: '55%', borderRadius: 6, animation: 'shimmer 1.5s infinite', backgroundSize: '200% 100%', backgroundImage: 'linear-gradient(90deg,#252A3D 25%,#2D3350 50%,#252A3D 75%)' }} />
      </div>
    </div>
  );
}

// ── Product Card ──────────────────────────────────────────────────────────────
function ProductCard({ product, inCart, onAdd, onChooseUnit, pricingTier = 'retail' }) {
  const [hovered, setHovered] = useState(false);
  const meta = catMeta(product.category);
  const sellableUnits = getSellableUnits(product);
  const defaultUnit = getDefaultSaleUnit(product);
  const defaultAvailable = getAvailableUnitCount(product, defaultUnit);
  const activePrice = getUnitActivePrice(product, defaultUnit, pricingTier);
  const hasOtherUnits = sellableUnits.length > 1;
  const isOut = product.quantity === 0 || sellableUnits.every((unit) => getAvailableUnitCount(product, unit) < 1);
  const isLow = product.quantity > 0 && product.quantity < 10;

  const stockBadge = isOut
    ? { bg: 'rgba(255,107,107,0.18)', color: '#FF6B6B', label: '0' }
    : isLow
    ? { bg: 'rgba(240,180,41,0.18)', color: '#F0B429', label: String(product.quantity) }
    : { bg: 'rgba(0,201,177,0.18)', color: '#00C9B1', label: String(product.quantity) };

  return (
    <div
      onClick={() => !isOut && onAdd(product)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: inCart ? 'rgba(0,201,177,0.06)' : '#1E2235',
        borderRadius: 14,
        overflow: 'hidden',
        cursor: isOut ? 'not-allowed' : 'pointer',
        opacity: isOut ? 0.45 : 1,
        transition: 'all 0.15s',
        transform: hovered && !isOut ? 'translateY(-2px)' : 'none',
        border: `1px solid ${inCart ? 'rgba(0,201,177,0.5)' : hovered && !isOut ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.06)'}`,
        position: 'relative',
        userSelect: 'none',
      }}
    >
      {/* Image/icon area */}
      <div style={{ height: 100, background: '#252A3D', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, position: 'relative', overflow: 'hidden' }}>
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fb = e.currentTarget.parentElement?.querySelector('[data-product-fallback]');
              if (fb) fb.style.display = 'flex';
            }}
          />
        ) : null}
        <div
          data-product-fallback="true"
          style={{ display: product.imageUrl ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', fontSize: 36, position: 'absolute', inset: 0 }}
        >
          {meta.icon}
        </div>
        <span style={{ position: 'absolute', top: 6, right: 6, fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 100, background: stockBadge.bg, color: stockBadge.color, zIndex: 1 }}>
          {stockBadge.label}
        </span>
        {inCart > 0 && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,201,177,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, zIndex: 2 }}>
            ✓
          </div>
        )}
      </div>
      {/* Info */}
      <div style={{ padding: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'white' }}>
          {product.name}
        </div>
        <div style={{ fontSize: 14, fontWeight: 900, color: '#F0B429', fontFamily: '"JetBrains Mono", monospace' }}>
          {fmt(activePrice)}
        </div>
        <div style={{ fontSize: 10, color: '#6B7A99', marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {isOut ? 'Out of stock' : `${defaultAvailable} ${String(defaultUnit?.name || 'unit').toLowerCase()}${defaultAvailable === 1 ? '' : 's'}`}
          </span>
          {hasOtherUnits && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChooseUnit(product); }}
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '2px 7px', fontSize: 9, fontWeight: 700, cursor: 'pointer', color: '#F0B429', flexShrink: 0 }}
            >
              {sellableUnits.length} units
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Cart Item ─────────────────────────────────────────────────────────────────
function CartItem({ item, onQtyChange, onRemove }) {
  const meta = catMeta(item.category);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, borderRadius: 12, background: '#1E2235', border: '1px solid rgba(255,255,255,0.06)', marginBottom: 8, animation: 'slideUp 200ms ease forwards' }}>
      <div style={{ width: 40, height: 40, borderRadius: 8, background: '#252A3D', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0, overflow: 'hidden' }}>
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : meta.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.name}
        </div>
        <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 1, fontFamily: '"JetBrains Mono", monospace' }}>
          {fmt(item.price)} × {item.qty}
          {item.baseQuantityPerUnit > 1 && ` · ${item.baseQuantityPerUnit} ${String(item.baseUnitName || 'base unit').toLowerCase()}s ea`}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <button onClick={() => onQtyChange(-1)} style={qtyBtnStyle}>−</button>
        <span style={{ fontSize: 13, fontWeight: 800, minWidth: 20, textAlign: 'center', fontFamily: '"JetBrains Mono", monospace', color: 'white' }}>{item.qty}</span>
        <button onClick={() => onQtyChange(1)} style={qtyBtnStyle}>+</button>
      </div>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#F0B429', fontFamily: '"JetBrains Mono", monospace', flexShrink: 0, minWidth: 54, textAlign: 'right' }}>
        {fmt(Number(item.price) * item.qty).replace('GHS ', '')}
      </div>
      <button
        onClick={onRemove}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.2)', fontSize: '0.9rem', padding: '2px 4px', borderRadius: 4, transition: 'color 150ms' }}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#FF6B6B'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.2)'; }}
      >✕</button>
    </div>
  );
}

const qtyBtnStyle = {
  width: 26, height: 26, borderRadius: 8,
  background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.06)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 14, fontWeight: 800, cursor: 'pointer',
  color: 'white', transition: 'all 0.15s',
  fontFamily: '"Cabinet Grotesk", sans-serif',
};

// ── Receipt Modal ─────────────────────────────────────────────────────────────
function ReceiptModal({ sale, onNewSale, businessName }) {
  const [emailInput, setEmailInput] = useState('');
  const [sendingEmail, setSendingEmail]   = useState(false);
  const [emailSent, setEmailSent]         = useState(false);
  const [emailError, setEmailError]       = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [phoneInput, setPhoneInput]       = useState(sale.customer?.phone || '');
  const [sendingSms, setSendingSms]       = useState(false);
  const [smsSent, setSmsSent]             = useState(false);
  const [smsError, setSmsError]           = useState('');
  const [showSmsForm, setShowSmsForm]     = useState(false);
  const [showPaymentLink, setShowPaymentLink] = useState(false);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

  const handleSendReceipt = async () => {
    if (!emailInput.trim()) return;
    setSendingEmail(true);
    setEmailError('');
    try {
      const r = await fetch(`${API_BASE}/sales/${sale.id}/receipt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ email: emailInput.trim() }),
      });
      const data = await r.json();
      if (!r.ok) { setEmailError(data.error || 'Failed to send'); return; }
      setEmailSent(true);
      setShowEmailForm(false);
    } catch {
      setEmailError('Network error');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendSmsReceipt = async () => {
    if (!phoneInput.trim()) return;
    setSendingSms(true);
    setSmsError('');
    try {
      const r = await fetch(`${API_BASE}/sales/${sale.id}/sms-receipt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ phone: phoneInput.trim() }),
      });
      const data = await r.json();
      if (!r.ok) { setSmsError(data.error || 'Failed to send'); return; }
      setSmsSent(true);
      setShowSmsForm(false);
    } catch {
      setSmsError('Network error');
    } finally {
      setSendingSms(false);
    }
  };

  const saleTime = sale.createdAt ? new Date(sale.createdAt) : new Date();
  const payLabel = { cash: 'Cash', momo: 'Mobile Money (MoMo)', card: 'Card', credit: 'Credit' };
  const receiptId = sale.offline ? sale.id : `#${String(sale.id).padStart(4, '0')}`;
  const saleStatus = sale.offline ? 'Offline sale' : 'Completed sale';
  const settlementStatus = sale.settlementStatus || (Number(sale.balanceDue || 0) > 0 ? 'partial' : sale.paymentMethod === 'credit' ? 'credit' : 'paid');
  const amountCollected = Number(sale.amountCollected ?? (sale.paymentMethod === 'cash' ? sale.amountTendered ?? sale.total : sale.total));
  const balanceDue = Number(sale.balanceDue ?? (settlementStatus === 'partial' ? Number(sale.total || 0) - amountCollected : settlementStatus === 'credit' ? sale.total : 0));
  const discountAmount = Number(sale.discountAmount || 0);
  const loyaltyDiscount = Number(sale.loyaltyDiscount || 0);
  const totalDiscount = Number(sale.totalDiscount ?? (discountAmount + loyaltyDiscount));
  const lineItems = sale.items.map((si, index) => {
    const quantity = Number(si.quantity || 0);
    const unitPrice = Number(si.price || 0);
    return {
      id: si.id || `${si.itemId || 'item'}-${index}`,
      name: si.item?.name || `Item #${si.itemId}`,
      unitName: si.unitName || 'Unit',
      quantity,
      unitPrice,
      lineTotal: quantity * unitPrice,
    };
  });
  const subtotal = Number(sale.subtotal ?? lineItems.reduce((sum, item) => sum + item.lineTotal, 0));
  const customerName = sale.customer?.name || 'Walk-in customer';

  const metaRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    fontSize: '0.74rem',
    marginBottom: 6,
  };

  const metaLabelStyle = {
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontWeight: 700,
    fontSize: '0.65rem',
  };

  return (
    <div
      id="receipt-print-overlay"
      style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
      backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 200, padding: 16,
    }}
    >
      <style>{`
        @media print {
          @page { size: 80mm auto; margin: 6mm; }

          html, body {
            background: #ffffff !important;
          }

          body * {
            visibility: hidden !important;
          }

          #receipt-print-root, #receipt-print-root * {
            visibility: visible !important;
          }

          #receipt-print-overlay {
            position: static !important;
            inset: auto !important;
            padding: 0 !important;
            background: #ffffff !important;
            backdrop-filter: none !important;
            display: block !important;
          }

          #receipt-print-root {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 68mm !important;
            max-width: 68mm !important;
            margin: 0 !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            overflow: visible !important;
          }

          .receipt-print-hide {
            display: none !important;
          }
        }
      `}</style>

      <div
        id="receipt-print-root"
        style={{
        background: 'white', borderRadius: 24, width: '100%', maxWidth: 380,
        maxHeight: '90vh', overflowY: 'auto',
        animation: 'modalEnter 350ms cubic-bezier(0.34,1.56,0.64,1) forwards',
        boxShadow: '0 24px 70px rgba(15,23,42,0.22)',
        border: '1px solid #E2E8F0',
      }}
      >
        {/* Offline warning banner */}
        {sale.offline && (
          <div style={{
            background: '#FFFBEB', borderBottom: '1px solid #FCD34D',
            padding: '9px 18px', textAlign: 'center',
            fontSize: '0.7rem', fontWeight: 800, color: '#92600A',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            Offline sale pending sync
          </div>
        )}

        {/* Receipt header */}
        <div style={{ padding: '24px 22px 18px', borderBottom: '1px dashed #CBD5E1' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{
                  width: 34,
                  height: 34,
                  borderRadius: 999,
                  border: '2px solid #D4A017',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#D4A017',
                  fontWeight: 800,
                  fontFamily: 'Sora, sans-serif',
                  fontSize: '0.82rem',
                }}>
                  OK
                </div>
                <div>
                  <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.02rem', color: '#0F172A', lineHeight: 1.1 }}>
                    {businessName || 'Sunrise General Store'}
                  </div>
                  <div style={{ fontSize: '0.66rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 3 }}>
                    Sales receipt
                  </div>
                </div>
              </div>
            </div>
            <div style={{
              padding: '5px 10px',
              borderRadius: 999,
              background: sale.offline ? '#FEF3C7' : '#DBEAFE',
              color: sale.offline ? '#92400E' : '#1D4ED8',
              fontSize: '0.64rem',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              whiteSpace: 'nowrap',
            }}>
              {saleStatus}
            </div>
          </div>

          <div style={metaRowStyle}>
            <span style={metaLabelStyle}>Receipt</span>
            <span style={{ color: '#0F172A', fontWeight: 700 }}>{receiptId}</span>
          </div>
          <div style={metaRowStyle}>
            <span style={metaLabelStyle}>Date</span>
            <span style={{ color: '#0F172A', fontWeight: 600 }}>
              {saleTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
          <div style={metaRowStyle}>
            <span style={metaLabelStyle}>Time</span>
            <span style={{ color: '#0F172A', fontWeight: 600 }}>
              {saleTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div style={metaRowStyle}>
            <span style={metaLabelStyle}>Customer</span>
            <span style={{ color: '#0F172A', fontWeight: 600, textAlign: 'right' }}>{customerName}</span>
          </div>
          <div style={metaRowStyle}>
            <span style={metaLabelStyle}>Payment</span>
            <span style={{ color: '#0F172A', fontWeight: 600, textAlign: 'right' }}>
              {settlementStatus === 'partial'
                ? `${payLabel[sale.paymentMethod] || sale.paymentMethod || 'Cash'} + Credit`
                : payLabel[sale.paymentMethod] || sale.paymentMethod || 'Cash'}
            </span>
          </div>
          <div style={metaRowStyle}>
            <span style={metaLabelStyle}>Settlement</span>
            <span style={{ color: '#0F172A', fontWeight: 600, textAlign: 'right' }}>
              {settlementStatus === 'partial' ? 'Part paid' : settlementStatus === 'credit' ? 'On credit' : 'Paid in full'}
            </span>
          </div>
        </div>

        {/* Items */}
        <div style={{ padding: '16px 22px 10px', borderBottom: '1px dashed #CBD5E1' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, fontSize: '0.66rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 800, marginBottom: 10 }}>
            <span>Item</span>
            <span>Total</span>
          </div>
          {lineItems.map((item) => (
            <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 700, color: '#0F172A', fontSize: '0.82rem', lineHeight: 1.3, marginBottom: 3 }}>
                  {item.name}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#64748B' }}>
                  {item.quantity} x {item.unitName} @ {fmt(item.unitPrice)}
                </div>
              </div>
              <div style={{ fontWeight: 800, fontFamily: 'Sora, sans-serif', color: '#1B3A6B', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                {fmt(item.lineTotal)}
              </div>
            </div>
          ))}
        </div>

        {/* Total */}
        <div style={{ padding: '16px 22px', borderBottom: '1px dashed #CBD5E1' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#64748B', marginBottom: 6 }}>
            <span>Subtotal</span>
            <span>{fmt(subtotal)}</span>
          </div>
          {discountAmount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#D97706', fontWeight: 700, marginBottom: 6 }}>
              <span>Discount</span>
              <span>− {fmt(discountAmount)}</span>
            </div>
          )}
          {loyaltyDiscount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#D97706', fontWeight: 700, marginBottom: 6 }}>
              <span>Loyalty discount</span>
              <span>− {fmt(loyaltyDiscount)}</span>
            </div>
          )}
          {discountAmount <= 0 && loyaltyDiscount <= 0 && subtotal !== Number(sale.total) && totalDiscount !== 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#D97706', fontWeight: 700, marginBottom: 6 }}>
              <span>Discount</span>
              <span>− {fmt(totalDiscount)}</span>
            </div>
          )}
          {((sale.paymentMethod === 'cash' && sale.amountTendered != null) || settlementStatus === 'partial') && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#64748B', marginBottom: 6 }}>
                <span>{settlementStatus === 'partial' ? 'Collected now' : 'Cash received'}</span>
                <span>{fmt(settlementStatus === 'partial' ? amountCollected : sale.amountTendered)}</span>
              </div>
              {settlementStatus === 'partial' ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#B45309', fontWeight: 700, marginBottom: 8 }}>
                  <span>Balance on credit</span>
                  <span>{fmt(balanceDue)}</span>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#065F46', fontWeight: 700, marginBottom: 8 }}>
                  <span>Change due</span>
                  <span>{fmt(sale.changeDue || 0)}</span>
                </div>
              )}
            </>
          )}
          {settlementStatus === 'credit' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#B45309', fontWeight: 700, marginBottom: 8 }}>
              <span>Balance on credit</span>
              <span>{fmt(balanceDue)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderTop: '1px solid #E2E8F0', paddingTop: 10 }}>
            <span style={{ fontWeight: 800, color: '#0F172A', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Total</span>
            <span style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.45rem', color: '#1B3A6B' }}>
              {fmt(sale.total)}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 22px 22px', textAlign: 'center' }}>
          <div style={{ fontSize: '0.72rem', color: '#475569', fontWeight: 600, marginBottom: 6 }}>
            Thank you for your purchase
          </div>
          <div style={{ fontSize: '0.66rem', color: '#94A3B8', lineHeight: 1.5, marginBottom: 14 }}>
            Please keep this receipt for your records.
          </div>

          {/* Digital receipt send */}
          {!sale.offline && (
            <div className="receipt-print-hide" style={{ marginBottom: 12 }}>
              {emailSent ? (
                <div style={{ background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.25)', borderRadius: 10, padding: '10px 14px', fontSize: '0.8rem', color: '#16A34A', fontWeight: 600, textAlign: 'center' }}>
                  ✓ Receipt sent to {emailInput}
                </div>
              ) : showEmailForm ? (
                <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', marginBottom: 8 }}>Send receipt to email</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => { setEmailInput(e.target.value); setEmailError(''); }}
                      onKeyDown={(e) => e.key === 'Enter' && handleSendReceipt()}
                      placeholder="customer@email.com"
                      autoFocus
                      style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1.5px solid #E2E8F0', fontSize: '0.82rem', outline: 'none' }}
                    />
                    <button
                      onClick={handleSendReceipt}
                      disabled={sendingEmail || !emailInput.trim()}
                      style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#1B3A6B', color: 'white', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap', opacity: sendingEmail || !emailInput.trim() ? 0.6 : 1 }}
                    >
                      {sendingEmail ? '…' : 'Send'}
                    </button>
                    <button
                      onClick={() => { setShowEmailForm(false); setEmailError(''); }}
                      style={{ padding: '8px 10px', borderRadius: 8, border: '1.5px solid #E2E8F0', background: 'white', fontSize: '0.8rem', cursor: 'pointer', color: '#64748B' }}
                    >
                      ✕
                    </button>
                  </div>
                  {emailError && <div style={{ fontSize: '0.73rem', color: '#DC2626', marginTop: 6 }}>{emailError}</div>}
                </div>
              ) : (
                <button
                  onClick={() => {
                    const prefill = sale.customer?.email || '';
                    setEmailInput(prefill);
                    setShowEmailForm(true);
                  }}
                  style={{ width: '100%', padding: '9px 0', borderRadius: 10, border: '1.5px dashed #CBD5E1', background: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem', color: '#64748B' }}
                >
                  ✉ Send receipt by email
                </button>
              )}
            </div>
          )}

          {/* SMS receipt */}
          {!sale.offline && (
            <div className="receipt-print-hide" style={{ marginBottom: 12 }}>
              {smsSent ? (
                <div style={{ background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.25)', borderRadius: 10, padding: '10px 14px', fontSize: '0.8rem', color: '#16A34A', fontWeight: 600, textAlign: 'center' }}>
                  ✓ Receipt sent to {phoneInput}
                </div>
              ) : showSmsForm ? (
                <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', marginBottom: 8 }}>Send receipt by text</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="tel"
                      value={phoneInput}
                      onChange={(e) => { setPhoneInput(e.target.value); setSmsError(''); }}
                      onKeyDown={(e) => e.key === 'Enter' && handleSendSmsReceipt()}
                      placeholder="0XX XXX XXXX"
                      autoFocus
                      style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1.5px solid #E2E8F0', fontSize: '0.82rem', outline: 'none' }}
                    />
                    <button
                      onClick={handleSendSmsReceipt}
                      disabled={sendingSms || !phoneInput.trim()}
                      style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#16A34A', color: 'white', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap', opacity: sendingSms || !phoneInput.trim() ? 0.6 : 1 }}
                    >
                      {sendingSms ? '…' : 'Send'}
                    </button>
                    <button
                      onClick={() => { setShowSmsForm(false); setSmsError(''); }}
                      style={{ padding: '8px 10px', borderRadius: 8, border: '1.5px solid #E2E8F0', background: 'white', fontSize: '0.8rem', cursor: 'pointer', color: '#64748B' }}
                    >
                      ✕
                    </button>
                  </div>
                  {smsError && <div style={{ fontSize: '0.73rem', color: '#DC2626', marginTop: 6 }}>{smsError}</div>}
                </div>
              ) : (
                <button
                  onClick={() => { setPhoneInput(sale.customer?.phone || ''); setShowSmsForm(true); }}
                  style={{ width: '100%', padding: '9px 0', borderRadius: 10, border: '1.5px dashed #CBD5E1', background: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem', color: '#64748B' }}
                >
                  💬 Send receipt by text
                </button>
              )}
            </div>
          )}

          {/* Request payment button for unpaid/partial balances */}
          {!sale.offline && balanceDue > 0 && (
            <div className="receipt-print-hide" style={{ marginBottom: 10 }}>
              <button
                onClick={() => setShowPaymentLink(true)}
                style={{ width: '100%', padding: '10px 0', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#1B3A6B,#2563EB)', color: 'white', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}
              >
                💳 Request payment — GHS {balanceDue.toFixed(2)} due
              </button>
            </div>
          )}

          <div className="receipt-print-hide" style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => window.print()}
              style={{
                flex: 1, padding: '10px 0', borderRadius: 10,
                border: '1.5px solid #E2E8F0', background: 'white',
                cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem', color: '#1B3A6B',
                transition: 'all 150ms',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F0F4FA'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
            >
              Print receipt
            </button>
            <button
              onClick={onNewSale}
              style={{
                flex: 1, padding: '10px 0', borderRadius: 10,
                border: 'none', background: '#1B3A6B',
                cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem', color: 'white',
                transition: 'all 150ms',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#D4A017'; e.currentTarget.style.color = '#000'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#1B3A6B'; e.currentTarget.style.color = 'white'; }}
            >
              Start next sale
            </button>
          </div>

          {showPaymentLink && (
            <PaymentLinkModal sale={sale} onClose={() => setShowPaymentLink(false)} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main POS Component ────────────────────────────────────────────────────────
export default function POS() {
  const [inventory, setInventory]       = useState([]);
  const [customers, setCustomers]       = useState([]);
  const [search, setSearch]             = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [cart, setCart]                 = useState([]);
  const [customerId, setCustomerId]     = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerForm, setCustomerForm] = useState(EMPTY_CUSTOMER_FORM);
  const [customerSaving, setCustomerSaving] = useState(false);
  const [customerError, setCustomerError] = useState('');
  const [unitPickerProduct, setUnitPickerProduct] = useState(null);
  const [staffId, setStaffId]           = useState('');
  const [staffList, setStaffList]       = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [referralSource, setReferralSource] = useState('');
  const [creditForm, setCreditForm]     = useState(EMPTY_CREDIT_FORM);
  const [discountForm, setDiscountForm] = useState(EMPTY_DISCOUNT_FORM);
  const [allowPartialPayment, setAllowPartialPayment] = useState(false);
  const [loading, setLoading]           = useState(true);
  const [submitting, setSubmitting]     = useState(false);
  const [receipt, setReceipt]           = useState(null);
  const [error, setError]               = useState('');
  const [user, setUser]                 = useState(null);
  const [loyaltySettings, setLoyaltySettings] = useState({ earnRate: 10, redeemRate: 100, redeemValue: 5, isEnabled: true });
  const [useLoyalty, setUseLoyalty]     = useState(false);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [amountTendered, setAmountTendered] = useState('');
  const [paymentConfig, setPaymentConfig] = useState({
    isActive: false,
    acceptCard: false,
    acceptMomo: false,
    provider: 'paystack',
  });
  const [paymentAttempt, setPaymentAttempt] = useState(null);

  const { isOnline } = useOnlineStatus();
  const searchRef   = useRef(null);
  const customerRef = useRef(null);

  // Keep a ref to filtered products to avoid stale closure in keydown handler
  const filteredRef = useRef([]);

  const finalizeSaleFromPayment = useCallback((sale) => {
    setInventory((prev) => prev.map((inv) => {
      const soldBaseQty = getReservedBaseQty(cart, inv.id);
      return soldBaseQty ? { ...inv, quantity: Math.max(0, inv.quantity - soldBaseQty) } : inv;
    }));
    setCart([]);
    setAmountTendered('');
    setCreditForm(EMPTY_CREDIT_FORM);
    setDiscountForm(EMPTY_DISCOUNT_FORM);
    setAllowPartialPayment(false);
    setUnitPickerProduct(null);
    setPaymentAttempt(null);
    setReferralSource('');
    setReceipt({
      ...sale,
      amountTendered: null,
      changeDue: 0,
    });
  }, [cart]);

  useEffect(() => {
    const load = async () => {
      try {
        const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
        if (storedUser) setUser(storedUser);
      } catch {
        // ignore parse issues
      }

      if (isOnline) {
        try {
          const [invRes, custRes, loyRes, staffRes, paymentRes] = await Promise.all([
            api.get('/inventory'),
            api.get('/customers'),
            api.get('/crm/loyalty/settings'),
            api.get('/staff').catch(() => ({ data: [] })),
            api.get('/workspaces/current/payment-config').catch(() => ({ data: null })),
          ]);
          setInventory(invRes.data);
          setCustomers(custRes.data);
          setLoyaltySettings(loyRes.data);
          setStaffList(staffRes.data || []);
          if (paymentRes.data) {
            setPaymentConfig({
              isActive: paymentRes.data.isActive,
              acceptCard: paymentRes.data.acceptCard,
              acceptMomo: paymentRes.data.acceptMomo,
              provider: paymentRes.data.provider || 'paystack',
            });
          }
          cacheInventory(invRes.data).catch(() => {});
          cacheCustomers(custRes.data).catch(() => {});
        } catch {
          // Fallback to cache if online fetch fails
          const [inv, cust] = await Promise.all([getOfflineInventory(), getOfflineCustomers()]);
          setInventory(inv);
          setCustomers(cust);
        }
      } else {
        const [inv, cust] = await Promise.all([getOfflineInventory(), getOfflineCustomers()]);
        setInventory(inv);
        setCustomers(cust);
      }
      setLoading(false);
    };
    load();
  }, [isOnline]);

  // Reset loyalty when customer changes
  useEffect(() => {
    setUseLoyalty(false);
    setPointsToRedeem(0);
  }, [customerId]);

  useEffect(() => {
    if (!paymentAttempt?.reference || paymentAttempt.status !== 'pending') return undefined;

    const interval = window.setInterval(async () => {
      try {
        const { data } = await api.get(`/payments/attempts/${paymentAttempt.reference}`);
        if (data.status === 'paid' && data.sale) {
          finalizeSaleFromPayment(data.sale);
        } else if (['failed', 'cancelled', 'paid_unfulfilled'].includes(data.status)) {
          setPaymentAttempt(null);
          setSubmitting(false);
          setError(data.status === 'paid_unfulfilled'
            ? 'Payment was captured, but the sale needs manual resolution. Please check your sales history.'
            : 'Payment was not completed. You can try again.');
        }
      } catch {
        // keep polling while the payment page is open
      }
    }, 5000);

    return () => window.clearInterval(interval);
  }, [finalizeSaleFromPayment, paymentAttempt]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== 'optika:last-sale-payment' || !event.newValue) return;
      try {
        const payload = JSON.parse(event.newValue);
        if (payload?.status === 'paid' && payload.sale) {
          finalizeSaleFromPayment(payload.sale);
        }
      } catch {
        // ignore malformed events
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [finalizeSaleFromPayment]);

  // Derived: categories
  const categories = ['All', ...new Set(inventory.map((i) => i.category).filter(Boolean))];

  // Derived: filtered products
  const filteredProducts = inventory.filter((item) => {
    const q = search.toLowerCase();
    const matchSearch = !q || item.name.toLowerCase().includes(q)
      || (item.category || '').toLowerCase().includes(q)
      || (item.sku || '').toLowerCase().includes(q);
    const matchCat = activeCategory === 'All' || item.category === activeCategory;
    return matchSearch && matchCat;
  });
  filteredRef.current = filteredProducts;

  const checkoutLocked = paymentAttempt?.status === 'pending';
  const selectedCustomer = customerId ? customers.find((c) => String(c.id) === String(customerId)) : null;
  const selectedCustomerPricingTier = getCustomerPricingTier(selectedCustomer);

  // Cart helpers
  const addToCart = useCallback((product, selectedUnit = null) => {
    if (checkoutLocked) return;
    const unit = selectedUnit || getDefaultSaleUnit(product);
    if (!unit || getAvailableUnitCount(product, unit) < 1) return;

    setCart((prev) => {
      const nextItem = buildCartItem(product, unit, selectedCustomerPricingTier);
      const existing = prev.find((i) => i.cartKey === nextItem.cartKey);
      const reservedOther = getReservedBaseQty(prev, product.id, nextItem.cartKey);
      const maxBaseQty = Number(product.quantity || 0);

      if (existing) {
        const proposedQty = existing.qty + 1;
        if ((reservedOther + (proposedQty * existing.baseQuantityPerUnit)) > maxBaseQty) return prev;
        return prev.map((i) => i.cartKey === existing.cartKey ? { ...i, qty: proposedQty, availableUnits: getAvailableUnitCount(product, unit) } : i);
      }

      if ((reservedOther + nextItem.baseQuantityPerUnit) > maxBaseQty) return prev;
      return [...prev, nextItem];
    });
  }, [checkoutLocked, selectedCustomerPricingTier]);

  const handleProductTap = useCallback((product) => {
    const defaultUnit = getDefaultSaleUnit(product);
    if (defaultUnit && getAvailableUnitCount(product, defaultUnit) > 0) {
      addToCart(product, defaultUnit);
      return;
    }
    setUnitPickerProduct(product);
  }, [addToCart]);

  const updateQty = (cartKey, delta) => {
    if (checkoutLocked) return;
    setCart((prev) => prev.map((i) => {
      if (i.cartKey !== cartKey) return i;
      const inventoryItem = inventory.find((inv) => inv.id === i.id);
      const maxBaseQty = Number(inventoryItem?.quantity || 0);
      const reservedOther = getReservedBaseQty(prev, i.id, cartKey);
      const newQty = i.qty + delta;
      if (newQty < 1) return i;
      if ((reservedOther + (newQty * i.baseQuantityPerUnit)) > maxBaseQty) return i;
      return { ...i, qty: newQty, availableUnits: getAvailableUnitCount(inventoryItem || i, { conversionQty: i.baseQuantityPerUnit }) };
    }));
  };

  const removeFromCart = (cartKey) => {
    if (checkoutLocked) return;
    setCart((prev) => prev.filter((i) => i.cartKey !== cartKey));
  };
  const clearCart = () => {
    if (checkoutLocked) return;
    setCart([]);
    setAmountTendered('');
    setCreditForm(EMPTY_CREDIT_FORM);
    setDiscountForm(EMPTY_DISCOUNT_FORM);
    setAllowPartialPayment(false);
    setUnitPickerProduct(null);
    setReferralSource('');
  };

  const cartTotal = cart.reduce((s, i) => s + Number(i.price) * i.qty, 0);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const parsedDiscountValue = parseMoney(discountForm.value);
  const discountAmount = (() => {
    if (parsedDiscountValue <= 0) return 0;
    if (discountForm.type === 'percent') {
      return Math.min(cartTotal, (cartTotal * parsedDiscountValue) / 100);
    }
    return Math.min(cartTotal, parsedDiscountValue);
  })();
  const hasDiscount = discountAmount > 0;
  const discountedSubtotal = Math.max(0, cartTotal - discountAmount);

  // Loyalty helpers
  const isCreditSale = paymentMethod === 'credit';
  const customerPoints = selectedCustomer?.loyaltyPoints ?? 0;
  const pointsWorth = (customerPoints / loyaltySettings.redeemRate) * Number(loyaltySettings.redeemValue);
  const maxRedeemable = Math.min(
    customerPoints,
    Math.floor(discountedSubtotal / Math.max(Number(loyaltySettings.redeemValue) || 0.01, 0.01)) * loyaltySettings.redeemRate
  );
  const loyaltyDiscount = useLoyalty ? (pointsToRedeem / loyaltySettings.redeemRate) * Number(loyaltySettings.redeemValue) : 0;
  const totalDiscount = discountAmount + loyaltyDiscount;
  const finalTotal = Math.max(0, discountedSubtotal - loyaltyDiscount);
  const rawTendered = amountTendered === '' ? finalTotal : parseMoney(amountTendered);
  const isElectronicPayment = ['card', 'momo'].includes(paymentMethod);
  const supportsPartialPayment = ['cash', 'card', 'momo'].includes(paymentMethod);
  const amountDue = allowPartialPayment && supportsPartialPayment
    ? Math.max(0, finalTotal - rawTendered)
    : paymentMethod === 'cash'
      ? Math.max(0, finalTotal - rawTendered)
      : 0;
  const changeDue = paymentMethod === 'cash' && !allowPartialPayment ? Math.max(0, rawTendered - finalTotal) : 0;
  const isPartialSale = allowPartialPayment && supportsPartialPayment && amountDue > 0;
  const showCollectedNowInput = paymentMethod === 'cash' || (isElectronicPayment && allowPartialPayment);
  const quickTenderOptions = [
    finalTotal,
    Math.ceil(finalTotal / 5) * 5,
    Math.ceil(finalTotal / 10) * 10,
    Math.ceil(finalTotal / 20) * 20,
  ].filter((value, index, list) => value > 0 && list.indexOf(value) === index).slice(0, 4);

  useEffect(() => {
    setCart((prev) => prev.map((cartItem) => {
      const latestProduct = inventory.find((item) => item.id === cartItem.id);
      if (!latestProduct) return cartItem;
      const latestUnit = getSellableUnits(latestProduct).find((unit) => (
        (cartItem.unitId != null && unit.id === cartItem.unitId)
        || (cartItem.unitName && unit.name === cartItem.unitName)
      )) || getDefaultSaleUnit(latestProduct);
      const repriced = buildCartItem(latestProduct, latestUnit, selectedCustomerPricingTier);
      return {
        ...repriced,
        qty: cartItem.qty,
      };
    }));
  }, [inventory, selectedCustomerPricingTier]);

  useEffect(() => {
    if (!useLoyalty) {
      if (pointsToRedeem !== 0) setPointsToRedeem(0);
      return;
    }
    if (pointsToRedeem > maxRedeemable) {
      setPointsToRedeem(maxRedeemable);
    }
  }, [useLoyalty, pointsToRedeem, maxRedeemable]);

  const resetCustomerModal = () => {
    setCustomerForm(EMPTY_CUSTOMER_FORM);
    setCustomerError('');
    setCustomerSaving(false);
    setShowCustomerModal(false);
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    if (customerSaving) return;

    if (!isOnline) {
      setCustomerError('Customer creation needs internet right now.');
      return;
    }

    if (!customerForm.name.trim()) {
      setCustomerError('Customer name is required.');
      return;
    }

    setCustomerSaving(true);
    setCustomerError('');

    try {
      const payload = {
        name: customerForm.name.trim(),
        phone: customerForm.phone.trim() || null,
        email: customerForm.email.trim() || null,
        pricingTier: customerForm.pricingTier,
      };
      const { data } = await api.post('/customers', payload);
      const nextCustomers = [data, ...customers.filter((customer) => customer.id !== data.id)];
      setCustomers(nextCustomers);
      cacheCustomers(nextCustomers).catch(() => {});
      setCustomerId(String(data.id));
      resetCustomerModal();
    } catch (err) {
      setCustomerError(err.response?.data?.error || 'Failed to create customer.');
      setCustomerSaving(false);
    }
  };

  // Charge
  const handleCharge = async () => {
    if (cart.length === 0 || submitting) return;
    if (checkoutLocked) {
      setError('Finish the current electronic payment before starting another one.');
      return;
    }
    setError('');
    setSubmitting(true);

    if (paymentMethod === 'cash' && amountDue > 0 && !allowPartialPayment) {
      setError(`Customer still owes ${fmt(amountDue)} before you can complete this sale.`);
      setSubmitting(false);
      return;
    }

    if ((isCreditSale || isPartialSale) && !customerId) {
      setError('Select a saved customer before completing a sale with balance due.');
      setSubmitting(false);
      return;
    }

    if (allowPartialPayment && supportsPartialPayment && rawTendered <= 0) {
      setError('Enter the amount collected now, or use Credit if nothing is being paid today.');
      setSubmitting(false);
      return;
    }

    if (allowPartialPayment && supportsPartialPayment && rawTendered >= finalTotal) {
      setError('Enter an upfront amount that is lower than the total, or turn off the balance toggle to charge the full amount.');
      setSubmitting(false);
      return;
    }

    if (discountForm.type === 'percent' && parsedDiscountValue > 100) {
      setError('Discount percent cannot be greater than 100.');
      setSubmitting(false);
      return;
    }

    if (hasDiscount && !discountForm.reason.trim()) {
      setError('Enter a reason for this discount before completing the sale.');
      setSubmitting(false);
      return;
    }

    if (isElectronicPayment) {
      if (!isOnline) {
        setError('Card and MoMo payments require an internet connection.');
        setSubmitting(false);
        return;
      }

      if (!paymentConfig.isActive) {
        setError('Connect your business Paystack account in Settings before accepting electronic payments.');
        setSubmitting(false);
        return;
      }

      if (paymentMethod === 'card' && !paymentConfig.acceptCard) {
        setError('Card payments are disabled for this business account.');
        setSubmitting(false);
        return;
      }

      if (paymentMethod === 'momo' && !paymentConfig.acceptMomo) {
        setError('MoMo payments are disabled for this business account.');
        setSubmitting(false);
        return;
      }

      try {
        const settlementType = isPartialSale ? 'partial' : 'paid';
        const { data } = await api.post('/payments/initialize', {
          customerId: parseInt(customerId) || null,
          staffId: parseInt(staffId) || null,
          items: cart.map((i) => ({
            itemId: i.id,
            quantity: i.qty,
            unitId: i.unitId,
            unitName: i.unitName,
            conversionQty: i.baseQuantityPerUnit,
            unitPrice: i.price,
            unitCost: i.unitCost,
          })),
          paymentMethod,
          settlementType,
          amountPaidNow: isPartialSale ? rawTendered : null,
          pointsRedeemed: useLoyalty ? pointsToRedeem : 0,
          discountType: hasDiscount ? discountForm.type : null,
          discountValue: hasDiscount ? parsedDiscountValue : 0,
          discountReason: hasDiscount ? discountForm.reason.trim() : null,
          customerEmail: selectedCustomer?.email || null,
          customerPhone: selectedCustomer?.phone || null,
          creditDueDate: isPartialSale ? (creditForm.dueDate || null) : null,
          creditNotes: isPartialSale ? (creditForm.notes.trim() || null) : null,
          referralSource: referralSource || null,
        });

        const popup = window.open(data.authorization_url, '_blank', 'noopener,noreferrer');
        setPaymentAttempt({
          reference: data.reference,
          authorizationUrl: data.authorization_url,
          paymentMethod,
          status: 'pending',
          settlementType: data.settlementType || settlementType,
          amount: data.amount,
          saleTotal: data.saleTotal,
          balanceDue: data.balanceDue,
          popupOpened: Boolean(popup),
        });

        if (!popup) {
          setError('Your browser blocked the payment window. Use the "Open checkout" button below to continue.');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Could not start electronic checkout.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (isOnline) {
      try {
        const settlementType = isCreditSale ? 'credit' : isPartialSale ? 'partial' : 'paid';
        const { data: sale } = await api.post('/sales', {
          customerId: parseInt(customerId) || null,
          staffId: parseInt(staffId) || null,
          items: cart.map((i) => ({
            itemId: i.id,
            quantity: i.qty,
            unitId: i.unitId,
            unitName: i.unitName,
            conversionQty: i.baseQuantityPerUnit,
            unitPrice: i.price,
            unitCost: i.unitCost,
          })),
          paymentMethod,
          settlementType,
          amountPaidNow: isPartialSale ? rawTendered : null,
          status: 'completed',
          pointsRedeemed: useLoyalty ? pointsToRedeem : 0,
          discountType: hasDiscount ? discountForm.type : null,
          discountValue: hasDiscount ? parsedDiscountValue : 0,
          discountReason: hasDiscount ? discountForm.reason.trim() : null,
          creditDueDate: (isCreditSale || isPartialSale) ? (creditForm.dueDate || null) : null,
          creditNotes: (isCreditSale || isPartialSale) ? (creditForm.notes.trim() || null) : null,
          referralSource: referralSource || null,
        });
        setInventory((prev) => prev.map((inv) => {
          const soldBaseQty = getReservedBaseQty(cart, inv.id);
          return soldBaseQty ? { ...inv, quantity: Math.max(0, inv.quantity - soldBaseQty) } : inv;
        }));
        setCart([]);
        setAmountTendered('');
        setCreditForm(EMPTY_CREDIT_FORM);
        setDiscountForm(EMPTY_DISCOUNT_FORM);
        setAllowPartialPayment(false);
        setUnitPickerProduct(null);
        setReceipt({
          ...sale,
          amountTendered: paymentMethod === 'cash' ? rawTendered : null,
          changeDue: paymentMethod === 'cash' ? changeDue : 0,
        });
      } catch (err) {
        setError(err.response?.data?.error || 'Sale failed — please try again');
      } finally {
        setSubmitting(false);
      }
    } else {
      try {
        const settlementType = isCreditSale ? 'credit' : isPartialSale ? 'partial' : 'paid';
        const saleData = {
          customerId: parseInt(customerId) || null,
          staffId: parseInt(staffId) || null,
          items: cart.map((i) => ({
            itemId: i.id,
            quantity: i.qty,
            price: i.price,
            unitId: i.unitId,
            unitName: i.unitName,
            conversionQty: i.baseQuantityPerUnit,
            unitPrice: i.price,
            unitCost: i.unitCost,
          })),
          paymentMethod,
          settlementType,
          amountPaidNow: isPartialSale ? rawTendered : null,
          status: 'completed',
          pointsRedeemed: useLoyalty ? pointsToRedeem : 0,
          discountType: hasDiscount ? discountForm.type : null,
          discountValue: hasDiscount ? parsedDiscountValue : 0,
          discountAmount,
          discountReason: hasDiscount ? discountForm.reason.trim() : null,
          loyaltyDiscount,
          totalDiscount,
          subtotal: cartTotal,
          total: finalTotal,
          creditDueDate: (isCreditSale || isPartialSale) ? (creditForm.dueDate || null) : null,
          creditNotes: (isCreditSale || isPartialSale) ? (creditForm.notes.trim() || null) : null,
          referralSource: referralSource || null,
        };
        const localId = await queueSale(saleData);

        for (const item of cart) {
          await updateOfflineItemQty(item.id, Math.max(0, Number(item.quantity || 0) - (item.qty * item.baseQuantityPerUnit)));
        }
        setInventory((prev) => prev.map((inv) => {
          const soldBaseQty = getReservedBaseQty(cart, inv.id);
          return soldBaseQty ? { ...inv, quantity: Math.max(0, inv.quantity - soldBaseQty) } : inv;
        }));

        const offlineSale = {
          id: `LOCAL-${localId}`,
          items: cart.map((i) => ({
            itemId: i.id,
            item: { name: i.name },
            price: i.price,
            quantity: i.qty,
            unitName: i.unitName,
            conversionQty: i.baseQuantityPerUnit,
            baseQuantity: i.qty * i.baseQuantityPerUnit,
          })),
          total: finalTotal,
          subtotal: cartTotal,
          discountType: hasDiscount ? discountForm.type : null,
          discountValue: hasDiscount ? parsedDiscountValue : 0,
          discountAmount,
          discountReason: hasDiscount ? discountForm.reason.trim() : null,
          loyaltyDiscount,
          totalDiscount,
          paymentMethod,
          settlementStatus: settlementType,
          amountCollected: settlementType === 'credit' ? 0 : settlementType === 'partial' ? rawTendered : finalTotal,
          balanceDue: settlementType === 'partial' ? amountDue : settlementType === 'credit' ? finalTotal : 0,
          customer: selectedCustomer || null,
          offline: true,
          amountTendered: paymentMethod === 'cash' ? rawTendered : null,
          changeDue: paymentMethod === 'cash' ? changeDue : 0,
        };
        setCart([]);
        setAmountTendered('');
        setCreditForm(EMPTY_CREDIT_FORM);
        setDiscountForm(EMPTY_DISCOUNT_FORM);
        setAllowPartialPayment(false);
        setUnitPickerProduct(null);
        setReceipt(offlineSale);
      } catch {
        setError('Failed to save offline sale — please try again');
      } finally {
        setSubmitting(false);
      }
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handle = (e) => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      const inInput = tag === 'input' || tag === 'select' || tag === 'textarea';

      // Focus search on printable key press (when not already in an input)
      if (!inInput && !e.ctrlKey && !e.metaKey && !e.altKey && e.key.length === 1) {
        searchRef.current?.focus();
        return; // let key propagate so it types into search
      }

      if (e.key === 'Escape') {
        setSearch('');
        searchRef.current?.blur();
      }

      if (e.key === 'Enter' && !e.ctrlKey) {
        const list = filteredRef.current.filter((p) => p.quantity > 0);
        if (list.length === 1) addToCart(list[0]);
      }

      if (e.key === 'F1') {
        e.preventDefault();
        customerRef.current?.focus();
      }

      if (e.key === 'F2') {
        e.preventDefault();
        const ids = PAYMENT_METHODS.map((m) => m.id).filter((id) => {
          if (id === 'card') return isOnline && paymentConfig.isActive && paymentConfig.acceptCard;
          if (id === 'momo') return isOnline && paymentConfig.isActive && paymentConfig.acceptMomo;
          return true;
        });
        setPaymentMethod((m) => ids[(ids.indexOf(m) + 1) % ids.length]);
      }

      if (e.key === 'F10' || (e.ctrlKey && e.key === 'Enter')) {
        e.preventDefault();
        handleCharge();
      }
    };

    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToCart, checkoutLocked, isOnline, paymentConfig]);

  const pmLabel = PAYMENT_METHODS.find((m) => m.id === paymentMethod)?.label || 'Cash';
  const isMethodEnabled = (methodId) => {
    if (methodId === 'card') return isOnline && paymentConfig.isActive && paymentConfig.acceptCard;
    if (methodId === 'momo') return isOnline && paymentConfig.isActive && paymentConfig.acceptMomo;
    return true;
  };

  return (
    <Layout>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 420px', height: 'calc(100vh - 60px)', overflow: 'hidden', background: '#0B0E17', position: 'relative' }}>
        {/* Gradient orbs */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, background: 'radial-gradient(ellipse 600px 500px at 10% 50%, rgba(27,58,107,0.5) 0%, transparent 65%), radial-gradient(ellipse 400px 400px at 90% 10%, rgba(240,180,41,0.07) 0%, transparent 65%)' }} />

        {/* ── LEFT: Product Browser ──────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', padding: 20, gap: 14, overflow: 'hidden', position: 'relative', zIndex: 1 }}>

          {/* Topbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => window.history.back()} style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, fontSize: 18, color: 'rgba(255,255,255,0.7)' }}>←</button>
            <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: '-0.5px', color: 'white' }}>
              Quick <em style={{ fontFamily: '"Instrument Serif", serif', fontStyle: 'italic', color: '#F0B429' }}>Sale</em>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 100, background: isOnline ? 'rgba(0,201,177,0.1)' : 'rgba(255,107,107,0.1)', border: `1px solid ${isOnline ? 'rgba(0,201,177,0.2)' : 'rgba(255,107,107,0.2)'}`, fontSize: 12, fontWeight: 700, color: isOnline ? '#00C9B1' : '#FF6B6B' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: isOnline ? '#00C9B1' : '#FF6B6B', display: 'inline-block', animation: isOnline ? 'blink 1.5s infinite' : 'none' }} />
                {isOnline ? 'Live' : 'Offline'}
              </div>
              <span style={{ fontSize: 11, color: '#6B7A99', background: 'rgba(255,255,255,0.04)', padding: '5px 10px', borderRadius: 100, border: '1px solid rgba(255,255,255,0.06)' }}>
                {filteredProducts.length} items
              </span>
            </div>
          </div>

          {/* Search */}
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', opacity: 0.4, pointerEvents: 'none', fontSize: 15 }}>🔍</span>
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products by name or scan barcode…"
              style={{ width: '100%', padding: '10px 36px 10px 40px', background: '#1E2235', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, color: 'white', fontSize: 14, fontFamily: '"Cabinet Grotesk", sans-serif', outline: 'none', transition: 'border-color 0.15s' }}
              onFocus={(e) => { e.target.style.borderColor = 'rgba(240,180,41,0.4)'; }}
              onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.06)'; }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6B7A99', fontSize: '0.9rem' }}>✕</button>
            )}
          </div>

          {/* Category tabs */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {categories.map((cat) => {
              const isActive = activeCategory === cat;
              const meta = cat === 'All' ? null : catMeta(cat);
              return (
                <button key={cat} onClick={() => setActiveCategory(cat)} style={{ padding: '7px 14px', borderRadius: 100, fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s', border: `1px solid ${isActive ? 'rgba(240,180,41,0.3)' : 'rgba(255,255,255,0.06)'}`, background: isActive ? 'rgba(240,180,41,0.12)' : 'rgba(255,255,255,0.03)', color: isActive ? '#F0B429' : '#6B7A99', fontFamily: '"Cabinet Grotesk", sans-serif' }}>
                  {cat !== 'All' && meta?.icon + ' '}{cat}
                </button>
              );
            })}
          </div>

          {/* Product grid */}
          <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
            {loading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                {Array.from({ length: 9 }).map((_, i) => <SkeletonProduct key={i} />)}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 24px', color: '#6B7A99' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🔍</div>
                <div style={{ fontWeight: 600 }}>No products found</div>
                <div style={{ fontSize: '0.82rem', marginTop: 4 }}>Try a different search or category</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                {filteredProducts.map((product) => {
                  const cartCountForProduct = cart.filter((i) => i.id === product.id).reduce((sum, item) => sum + item.qty, 0);
                  return (
                    <ProductCard
                      key={product.id}
                      product={product}
                      inCart={cartCountForProduct || 0}
                      onAdd={handleProductTap}
                      onChooseUnit={setUnitPickerProduct}
                      pricingTier={selectedCustomerPricingTier}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Shortcuts */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            {[{ key: 'Any key', hint: 'Search' }, { key: 'Esc', hint: 'Clear' }, { key: 'Enter', hint: 'Add 1 match' }, { key: 'F1', hint: 'Customer' }, { key: 'F2', hint: 'Payment' }, { key: 'F10', hint: 'Charge' }].map(({ key, hint }) => (
              <span key={key} style={{ fontSize: 10, color: '#3D4559', display: 'flex', alignItems: 'center', gap: 4 }}>
                <kbd style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontFamily: '"JetBrains Mono", monospace', color: '#6B7A99' }}>{key}</kbd>
                {hint}
              </span>
            ))}
          </div>
        </div>

        {/* ── RIGHT: Cart ────────────────────────────────────────────────── */}
        <div style={{
          width: 420,
          borderLeft: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', flexDirection: 'column',
          background: 'rgba(15,20,35,0.98)',
          height: '100%', minHeight: 0, overflow: 'hidden',
          position: 'relative', zIndex: 1,
        }}>

          {/* Cart header */}
          <div style={{
            padding: '16px 18px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            position: 'sticky', top: 0, zIndex: 3,
            background: 'rgba(15,20,35,0.98)',
            backdropFilter: 'blur(10px)',
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: '"Cabinet Grotesk", sans-serif', fontWeight: 800, fontSize: '1rem', color: '#FFFFFF' }}>Current Sale</span>
                {cartCount > 0 && (
                  <span style={{
                    background: 'rgba(240,180,41,0.15)', color: '#F0B429',
                    fontSize: '0.65rem', fontWeight: 800,
                    padding: '2px 8px', borderRadius: 20,
                    border: '1px solid rgba(240,180,41,0.3)',
                    fontFamily: '"JetBrains Mono", monospace',
                  }}>
                    {cartCount} item{cartCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.66rem', color: '#6B7A99', marginTop: 2 }}>
                {new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
              </div>
            </div>
            {cart.length > 0 && (
              <button onClick={clearCart} style={{
                background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)',
                borderRadius: 8, cursor: 'pointer',
                fontSize: '0.7rem', color: '#FF6B6B', fontWeight: 700,
                padding: '4px 10px', transition: 'all 150ms',
                fontFamily: '"Cabinet Grotesk", sans-serif',
              }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,107,107,0.18)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,107,107,0.08)'; }}
              >
                Clear all
              </button>
            )}
          </div>

          <div style={{
            flex: 1, minHeight: 0,
            overflowY: 'auto', overflowX: 'hidden',
            paddingBottom: 16, scrollbarGutter: 'stable',
          }}>

          {/* Customer selector */}
          <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <label style={{ fontSize: '0.62rem', fontWeight: 700, color: '#6B7A99', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 7 }}>
              Customer (F1)
            </label>
            <select
              ref={customerRef}
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              style={{
                width: '100%', padding: '9px 12px',
                border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
                fontSize: '0.82rem', fontFamily: '"Cabinet Grotesk", sans-serif',
                outline: 'none', background: '#1E2235', color: '#E2E8F0',
                cursor: 'pointer', transition: 'border-color 150ms', boxSizing: 'border-box',
              }}
              onFocus={(e) => { e.target.style.borderColor = '#F0B429'; }}
              onBlur={(e)  => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; }}
            >
              <option value="">👤 Walk-in customer</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ''}{normalizePricingTier(c.pricingTier) !== 'retail' ? ` · ${pricingTierLabel(c.pricingTier)}` : ''}</option>
              ))}
            </select>
            {selectedCustomer && (
              <div style={{ marginTop: 7, fontSize: '0.7rem', color: '#00C9B1', fontWeight: 700 }}>
                {pricingTierLabel(selectedCustomerPricingTier)} pricing active for {selectedCustomer.name}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                onClick={() => {
                  setCustomerError('');
                  setCustomerForm(EMPTY_CUSTOMER_FORM);
                  setShowCustomerModal(true);
                }}
                style={{
                  border: 'none', background: 'none',
                  color: '#F0B429', fontSize: '0.74rem', fontWeight: 800,
                  cursor: 'pointer', padding: 0,
                  fontFamily: '"Cabinet Grotesk", sans-serif',
                }}
              >
                + New customer
              </button>
              <span style={{ fontSize: '0.66rem', color: isOnline ? '#6B7A99' : '#FF6B6B' }}>
                {isOnline ? 'Save repeat buyers here' : 'Needs internet'}
              </span>
            </div>
          </div>

          {/* Staff selector */}
          {staffList.length > 0 && (
            <div style={{ padding: '12px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <label style={{ fontSize: '0.62rem', fontWeight: 700, color: '#6B7A99', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 7 }}>
                Served By
              </label>
              <select
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                style={{
                  width: '100%', padding: '9px 12px',
                  border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
                  fontSize: '0.82rem', fontFamily: '"Cabinet Grotesk", sans-serif',
                  outline: 'none', background: '#1E2235', color: '#E2E8F0',
                  cursor: 'pointer', transition: 'border-color 150ms', boxSizing: 'border-box',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#F0B429'; }}
                onBlur={(e)  => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; }}
              >
                <option value="">— No staff assigned —</option>
                {staffList.map((s) => (
                  <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>
                ))}
              </select>
            </div>
          )}

          {/* Loyalty info */}
          {selectedCustomer && loyaltySettings.isEnabled && customerPoints > 0 && (
            <div style={{ padding: '12px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(240,180,41,0.05)' }}>
              <div style={{ fontSize: '0.72rem', color: '#F0B429', fontWeight: 600, marginBottom: 6 }}>
                ⭐ {selectedCustomer.name} has <strong>{customerPoints}</strong> points (worth <strong>GHS {pointsWorth.toFixed(2)}</strong>)
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, color: '#FFCD3C' }}>
                <input
                  type="checkbox"
                  checked={useLoyalty}
                  onChange={(e) => {
                    setUseLoyalty(e.target.checked);
                    if (e.target.checked && maxRedeemable > 0) setPointsToRedeem(maxRedeemable);
                    else setPointsToRedeem(0);
                  }}
                  style={{ accentColor: '#F0B429', width: 14, height: 14 }}
                />
                Use loyalty points
              </label>
              {useLoyalty && maxRedeemable > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: '0.65rem', color: '#F0B429' }}>Points to redeem</span>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#FFCD3C', fontFamily: '"JetBrains Mono", monospace' }}>
                      − GHS {loyaltyDiscount.toFixed(2)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={maxRedeemable}
                    step={loyaltySettings.redeemRate}
                    value={pointsToRedeem}
                    onChange={(e) => setPointsToRedeem(Number(e.target.value))}
                    style={{ width: '100%', accentColor: '#F0B429', cursor: 'pointer' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', color: '#FFCD3C', marginTop: 2 }}>
                    <span>0 pts</span>
                    <span style={{ fontWeight: 700 }}>{pointsToRedeem} pts</span>
                    <span>{maxRedeemable} pts</span>
                  </div>
                </div>
              )}
              {useLoyalty && maxRedeemable === 0 && (
                <div style={{ fontSize: '0.65rem', color: '#F0B429', marginTop: 4, opacity: 0.7 }}>
                  Not enough points for this cart amount
                </div>
              )}
            </div>
          )}

          {/* Cart items */}
          <div style={{ padding: '0 14px', marginTop: 10 }}>
            {cart.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 16px', color: '#3D4559' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 10 }}>🛍️</div>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#6B7A99' }}>Cart is empty</div>
                <div style={{ fontSize: '0.72rem', marginTop: 4, color: '#3D4559' }}>Click a product to add</div>
              </div>
            ) : (
              cart.map((item) => (
                <CartItem
                  key={item.cartKey}
                  item={item}
                  onQtyChange={(delta) => updateQty(item.cartKey, delta)}
                  onRemove={() => removeFromCart(item.cartKey)}
                />
              ))
            )}
          </div>

          {/* Summary */}
          {cart.length > 0 && (
            <div style={{ padding: '14px 18px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.76rem', color: '#6B7A99', marginBottom: 6 }}>
                <span>Subtotal ({cartCount} item{cartCount !== 1 ? 's' : ''})</span>
                <span style={{ fontFamily: '"JetBrains Mono", monospace' }}>{fmt(cartTotal)}</span>
              </div>

              {/* Discount block */}
              <div style={{ marginBottom: 10, padding: 12, borderRadius: 12, background: '#1E2235', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6B7A99', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                    Discount
                  </span>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: hasDiscount ? '#F0B429' : '#3D4559', fontFamily: '"JetBrains Mono", monospace' }}>
                    {hasDiscount ? `− ${fmt(discountAmount)}` : 'Optional'}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 8, marginBottom: 8 }}>
                  <select
                    value={discountForm.type}
                    onChange={(e) => setDiscountForm((prev) => ({ ...prev, type: e.target.value }))}
                    style={{
                      width: '100%', padding: '9px 10px',
                      border: '1px solid rgba(255,255,255,0.08)', borderRadius: 9,
                      fontSize: '0.82rem', fontFamily: '"Cabinet Grotesk", sans-serif',
                      outline: 'none', background: '#252A3D', color: '#E2E8F0',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="amount">Amount</option>
                    <option value="percent">Percent</option>
                  </select>
                  <input
                    type="number"
                    min="0"
                    max={discountForm.type === 'percent' ? '100' : undefined}
                    step="0.01"
                    value={discountForm.value}
                    onChange={(e) => setDiscountForm((prev) => ({ ...prev, value: e.target.value }))}
                    placeholder={discountForm.type === 'percent' ? '0' : '0.00'}
                    style={{
                      width: '100%', padding: '9px 10px',
                      border: '1px solid rgba(255,255,255,0.08)', borderRadius: 9,
                      fontSize: '0.82rem', fontFamily: '"JetBrains Mono", monospace',
                      outline: 'none', background: '#252A3D', color: '#E2E8F0',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <input
                  type="text"
                  value={discountForm.reason}
                  onChange={(e) => setDiscountForm((prev) => ({ ...prev, reason: e.target.value }))}
                  placeholder="Reason for discount"
                  style={{
                    width: '100%', padding: '9px 10px',
                    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 9,
                    fontSize: '0.82rem', fontFamily: '"Cabinet Grotesk", sans-serif',
                    outline: 'none', background: '#252A3D', color: '#E2E8F0',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {hasDiscount && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.74rem', color: '#F0B429', fontWeight: 600, marginBottom: 4 }}>
                  <span>Discount</span>
                  <span style={{ fontFamily: '"JetBrains Mono", monospace' }}>− {fmt(discountAmount)}</span>
                </div>
              )}
              {loyaltyDiscount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.74rem', color: '#F0B429', fontWeight: 600, marginBottom: 4 }}>
                  <span>⭐ Loyalty ({pointsToRedeem} pts)</span>
                  <span style={{ fontFamily: '"JetBrains Mono", monospace' }}>− {fmt(loyaltyDiscount)}</span>
                </div>
              )}

              {/* Total band */}
              <div style={{
                borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 10, paddingTop: 12,
                background: 'rgba(240,180,41,0.06)', borderRadius: 10, padding: '12px 14px',
                border: '1px solid rgba(240,180,41,0.15)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontFamily: '"Cabinet Grotesk", sans-serif', fontWeight: 700, fontSize: '0.82rem', color: '#6B7A99', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Total</span>
                <span style={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 800, fontSize: '1.3rem', color: '#F0B429' }}>
                  {fmt(finalTotal)}
                </span>
              </div>

              {showCollectedNowInput && (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: '#1E2235', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6B7A99', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                      {paymentMethod === 'cash'
                        ? (allowPartialPayment ? 'Amount Paid Now' : 'Cash Received')
                        : paymentMethod === 'momo'
                          ? 'Amount to Collect by MoMo'
                          : 'Amount to Charge Now'}
                    </span>
                    <span style={{ fontSize: '0.66rem', color: '#6B7A99' }}>
                      {paymentMethod === 'cash'
                        ? (allowPartialPayment ? 'Rest → balance' : 'Exact = leave blank')
                        : 'Rest → balance'}
                    </span>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={amountTendered}
                    onChange={(e) => setAmountTendered(e.target.value)}
                    placeholder={finalTotal.toFixed(2)}
                    style={{
                      width: '100%', padding: '10px 12px',
                      border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
                      fontSize: '0.92rem', fontFamily: '"JetBrains Mono", monospace',
                      outline: 'none', background: '#252A3D', color: '#FFFFFF',
                      boxSizing: 'border-box', marginBottom: 10,
                    }}
                  />
                  {paymentMethod === 'cash' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 10 }}>
                      {quickTenderOptions.map((value) => (
                        <button
                          key={value}
                          onClick={() => setAmountTendered(value.toFixed(2))}
                          style={{
                            border: '1px solid rgba(255,255,255,0.08)',
                            background: '#252A3D', borderRadius: 9,
                            padding: '7px 4px', cursor: 'pointer',
                            fontSize: '0.68rem', fontWeight: 700,
                            color: '#E2E8F0', fontFamily: '"JetBrains Mono", monospace',
                            transition: 'all 150ms',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#F0B429'; e.currentTarget.style.color = '#F0B429'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#E2E8F0'; }}
                        >
                          {fmt(value)}
                        </button>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: 4 }}>
                    <span style={{ color: '#6B7A99' }}>Amount entered</span>
                    <span style={{ fontWeight: 700, color: '#E2E8F0', fontFamily: '"JetBrains Mono", monospace' }}>{fmt(rawTendered)}</span>
                  </div>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem',
                    color: amountDue > 0 ? (allowPartialPayment ? '#F0B429' : '#FF6B6B') : '#00C9B1',
                    fontWeight: 800,
                  }}>
                    <span>{amountDue > 0 ? (allowPartialPayment ? 'Balance to collect later' : 'Balance due') : 'Change due'}</span>
                    <span style={{ fontFamily: '"JetBrains Mono", monospace' }}>{fmt(amountDue > 0 ? amountDue : changeDue)}</span>
                  </div>
                  {paymentMethod === 'cash' && (
                    <>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700, color: '#F0B429', marginTop: 10 }}>
                        <input
                          type="checkbox"
                          checked={allowPartialPayment}
                          onChange={(e) => {
                            setAllowPartialPayment(e.target.checked);
                            if (!e.target.checked) setCreditForm(EMPTY_CREDIT_FORM);
                          }}
                          style={{ accentColor: '#F0B429', width: 14, height: 14 }}
                        />
                        Turn unpaid amount into customer balance
                      </label>
                      {allowPartialPayment && (
                        <div style={{ fontSize: '0.66rem', color: '#6B7A99', marginTop: 5 }}>
                          Save part of this sale as credit without leaving checkout.
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Source of business */}
          <div style={{ padding: '14px 18px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#6B7A99', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
              Source of Business
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {REFERRAL_SOURCES.map((s) => {
                const active = referralSource === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setReferralSource(active ? '' : s.id)}
                    style={{
                      padding: '5px 10px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 700,
                      cursor: 'pointer', fontFamily: '"Cabinet Grotesk", sans-serif',
                      border: active ? '1.5px solid rgba(0,201,177,0.5)' : '1px solid rgba(255,255,255,0.08)',
                      background: active ? 'rgba(0,201,177,0.15)' : 'rgba(255,255,255,0.04)',
                      color: active ? '#00C9B1' : '#6B7A99',
                      transition: 'all 120ms',
                    }}
                  >
                    {s.icon} {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Payment method */}
          <div style={{ padding: '14px 18px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#6B7A99', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
              Payment Method (F2)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {PAYMENT_METHODS.map((m) => {
                const isActive = paymentMethod === m.id;
                const isMomo = m.id === 'momo';
                const isEnabled = isMethodEnabled(m.id);
                return (
                  <button
                    key={m.id}
                    onClick={() => {
                      if (!isEnabled || checkoutLocked) return;
                      setPaymentMethod(m.id);
                      if (m.id === 'credit') setAllowPartialPayment(false);
                    }}
                    style={{
                      padding: '10px 8px', borderRadius: 10,
                      cursor: isEnabled && !checkoutLocked ? 'pointer' : 'not-allowed',
                      border: isActive ? '1.5px solid rgba(240,180,41,0.5)' : '1px solid rgba(255,255,255,0.07)',
                      background: isActive
                        ? (isMomo ? 'rgba(240,180,41,0.15)' : 'rgba(240,180,41,0.12)')
                        : '#1E2235',
                      color: isActive ? '#F0B429' : (isEnabled ? '#6B7A99' : '#3D4559'),
                      fontWeight: isActive ? 800 : 500, fontSize: '0.76rem',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      transition: 'all 150ms', opacity: isEnabled ? 1 : 0.4,
                      fontFamily: '"Cabinet Grotesk", sans-serif',
                    }}
                    disabled={!isEnabled || checkoutLocked}
                  >
                    {m.icon} {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {(isCreditSale || allowPartialPayment) && (
            <div style={{ margin: '0 18px 12px', padding: '12px 14px', borderRadius: 10, background: 'rgba(240,180,41,0.06)', border: '1px solid rgba(240,180,41,0.2)' }}>
              <div style={{ fontSize: '0.76rem', fontWeight: 800, color: '#FFCD3C', marginBottom: 4 }}>
                {isCreditSale ? 'Track balance for this buyer' : 'Track remaining balance'}
              </div>
              <div style={{ fontSize: '0.7rem', color: '#6B7A99', lineHeight: 1.5, marginBottom: 10 }}>
                {selectedCustomer
                  ? isCreditSale
                    ? `${selectedCustomer.name} will be added to customer balances after this sale.`
                    : `${selectedCustomer.name} will owe the remaining ${fmt(amountDue)} after checkout.`
                  : isCreditSale
                    ? 'Credit sales need a saved customer so OptiKa can track the balance.'
                    : 'Select a saved customer so the remaining balance can be tracked.'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: '#6B7A99', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={creditForm.dueDate}
                    onChange={(e) => setCreditForm((prev) => ({ ...prev, dueDate: e.target.value }))}
                    style={{
                      width: '100%', padding: '9px 10px',
                      border: '1px solid rgba(240,180,41,0.25)', borderRadius: 9,
                      fontSize: '0.8rem', fontFamily: '"Cabinet Grotesk", sans-serif',
                      outline: 'none', boxSizing: 'border-box',
                      background: '#252A3D', color: '#E2E8F0',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: '#6B7A99', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Credit Note
                  </label>
                  <textarea
                    value={creditForm.notes}
                    onChange={(e) => setCreditForm((prev) => ({ ...prev, notes: e.target.value }))}
                    placeholder="Optional note for this pay-later sale"
                    rows={2}
                    style={{
                      width: '100%', padding: '9px 10px',
                      border: '1px solid rgba(240,180,41,0.25)', borderRadius: 9,
                      fontSize: '0.8rem', fontFamily: '"Cabinet Grotesk", sans-serif',
                      outline: 'none', boxSizing: 'border-box', resize: 'vertical',
                      background: '#252A3D', color: '#E2E8F0',
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {isElectronicPayment && (
            <div style={{ margin: '0 18px 12px', padding: '12px 14px', borderRadius: 10, background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.15)' }}>
              <div style={{ fontSize: '0.76rem', fontWeight: 700, color: '#38BDF8', marginBottom: 4 }}>
                Business-connected checkout
              </div>
              <div style={{ fontSize: '0.72rem', color: '#6B7A99', lineHeight: 1.5 }}>
                {allowPartialPayment
                  ? paymentMethod === 'momo'
                    ? 'OptiKa will collect the amount entered through your business Paystack MoMo checkout and save the rest as customer credit.'
                    : 'OptiKa will charge the amount entered through your business Paystack card checkout and save the rest as customer credit.'
                  : paymentMethod === 'momo'
                    ? 'OptiKa will open your business Paystack checkout for customer mobile money payment.'
                    : 'OptiKa will open your business Paystack checkout for secure card payment.'}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700, color: '#38BDF8', marginTop: 10 }}>
                <input
                  type="checkbox"
                  checked={allowPartialPayment}
                  onChange={(e) => {
                    setAllowPartialPayment(e.target.checked);
                    if (!e.target.checked) setCreditForm(EMPTY_CREDIT_FORM);
                  }}
                  style={{ accentColor: '#38BDF8', width: 14, height: 14 }}
                />
                Charge part now and save the rest as customer balance
              </label>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ margin: '0 18px', padding: '8px 12px', borderRadius: 9, background: 'rgba(255,107,107,0.1)', color: '#FF6B6B', fontSize: '0.74rem', fontWeight: 600, border: '1px solid rgba(255,107,107,0.2)' }}>
              {error}
            </div>
          )}

          {paymentAttempt?.status === 'pending' && (
            <div style={{ margin: '12px 18px 0', padding: '12px 14px', borderRadius: 10, background: 'rgba(240,180,41,0.08)', border: '1px solid rgba(240,180,41,0.2)' }}>
              <div style={{ fontSize: '0.74rem', fontWeight: 800, color: '#FFCD3C', marginBottom: 6 }}>
                Waiting for {paymentAttempt.paymentMethod === 'momo' ? 'MoMo' : 'card'} payment confirmation
              </div>
              <div style={{ fontSize: '0.7rem', color: '#6B7A99', lineHeight: 1.5, marginBottom: 10 }}>
                Reference: <strong style={{ color: '#E2E8F0' }}>{paymentAttempt.reference}</strong><br />
                {paymentAttempt.settlementType === 'partial' && (
                  <>
                    Charging now: <strong style={{ color: '#E2E8F0' }}>{fmt(paymentAttempt.amount)}</strong><br />
                    Balance after checkout: <strong style={{ color: '#E2E8F0' }}>{fmt(paymentAttempt.balanceDue)}</strong><br />
                  </>
                )}
                Keep this cart open while the customer completes payment. We'll confirm it automatically.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => window.open(paymentAttempt.authorizationUrl, '_blank', 'noopener,noreferrer')}
                  style={{
                    flex: 1, border: '1px solid rgba(255,255,255,0.1)',
                    background: '#252A3D', color: '#E2E8F0',
                    borderRadius: 9, padding: '9px 12px',
                    cursor: 'pointer', fontSize: '0.74rem', fontWeight: 700,
                    fontFamily: '"Cabinet Grotesk", sans-serif',
                    transition: 'all 150ms',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#F0B429'; e.currentTarget.style.color = '#F0B429'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#E2E8F0'; }}
                >
                  Open checkout
                </button>
                <button
                  onClick={async () => {
                    try {
                      const { data } = await api.post('/payments/verify', { reference: paymentAttempt.reference });
                      if (data.sale) finalizeSaleFromPayment(data.sale);
                    } catch (err) {
                      setError(err.response?.data?.error || 'Payment not completed yet.');
                    }
                  }}
                  style={{
                    flex: 1, border: 'none',
                    background: '#F0B429', color: '#0B0E17',
                    borderRadius: 9, padding: '9px 12px',
                    cursor: 'pointer', fontSize: '0.74rem', fontWeight: 800,
                    fontFamily: '"Cabinet Grotesk", sans-serif',
                  }}
                >
                  Check now
                </button>
              </div>
            </div>
          )}

          {/* Charge button */}
          <div style={{ padding: '14px 18px 18px' }}>
            <button
              onClick={handleCharge}
              disabled={cart.length === 0 || submitting || checkoutLocked}
              style={{
                width: '100%', padding: '15px 16px', borderRadius: 14, border: 'none',
                cursor: cart.length === 0 || checkoutLocked ? 'default' : 'pointer',
                background: cart.length === 0 || checkoutLocked
                  ? 'rgba(255,255,255,0.05)'
                  : '#F0B429',
                color: cart.length === 0 || checkoutLocked ? '#3D4559' : '#0B0E17',
                fontFamily: '"Cabinet Grotesk", sans-serif', fontWeight: 800, fontSize: '0.95rem',
                transition: 'all 200ms ease',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                letterSpacing: '0.02em',
              }}
              onMouseEnter={(e) => { if (cart.length > 0 && !submitting && !checkoutLocked) { e.currentTarget.style.background = '#FFCD3C'; e.currentTarget.style.transform = 'translateY(-1px)'; }}}
              onMouseLeave={(e) => { if (cart.length > 0 && !submitting && !checkoutLocked) { e.currentTarget.style.background = '#F0B429'; e.currentTarget.style.transform = 'none'; }}}
            >
              {submitting ? (
                <>
                  <span style={{ width: 16, height: 16, border: '2px solid rgba(11,14,23,0.3)', borderTopColor: '#0B0E17', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                  Processing...
                </>
              ) : checkoutLocked ? (
                'Awaiting payment confirmation...'
              ) : cart.length === 0 ? (
                'Add items to charge'
              ) : (
                `Charge ${fmt(finalTotal)} · ${pmLabel.toUpperCase()}`
              )}
            </button>
          </div>
          </div>
        </div>
      </div>

      {/* Receipt Modal */}
      {receipt && (
        <ReceiptModal
          sale={receipt}
          businessName={user?.businessName}
          onNewSale={() => {
            setReceipt(null);
            setCustomerId('');
            setStaffId('');
            setUseLoyalty(false);
            setPointsToRedeem(0);
            setAmountTendered('');
            setCreditForm(EMPTY_CREDIT_FORM);
            setDiscountForm(EMPTY_DISCOUNT_FORM);
            setAllowPartialPayment(false);
            setUnitPickerProduct(null);
          }}
        />
      )}

      {unitPickerProduct && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(7,10,18,0.75)',
            backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 215, padding: 16,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setUnitPickerProduct(null); }}
        >
          <div
            style={{
              width: '100%', maxWidth: 460,
              background: '#1E2235',
              borderRadius: 22,
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 32px 64px rgba(0,0,0,0.5)',
              padding: 24,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontFamily: '"Cabinet Grotesk", sans-serif', fontSize: '1.08rem', fontWeight: 800, color: '#FFFFFF' }}>
                  Choose Sale Unit
                </div>
                <div style={{ fontSize: '0.76rem', color: '#6B7A99', marginTop: 5 }}>
                  {unitPickerProduct.name} · {Number(unitPickerProduct.quantity || 0)} {String(unitPickerProduct.baseUnitName || 'base units').toLowerCase()} in stock
                </div>
                {selectedCustomer && (
                  <div style={{ fontSize: '0.7rem', color: '#00C9B1', fontWeight: 700, marginTop: 5 }}>
                    {pricingTierLabel(selectedCustomerPricingTier)} pricing
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setUnitPickerProduct(null)}
                style={{
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: '#252A3D', color: '#6B7A99',
                  width: 32, height: 32, borderRadius: 10,
                  cursor: 'pointer', fontWeight: 800, flexShrink: 0,
                  transition: 'all 150ms',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#FF6B6B'; e.currentTarget.style.borderColor = 'rgba(255,107,107,0.3)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#6B7A99'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'grid', gap: 10 }}>
              {getSellableUnits(unitPickerProduct).map((unit) => {
                const availableCount = getAvailableUnitCount(unitPickerProduct, unit);
                const activeUnitPrice = getUnitActivePrice(unitPickerProduct, unit, selectedCustomerPricingTier);
                const isUnavailable = availableCount < 1;
                return (
                  <button
                    key={`${unitPickerProduct.id}-${unit.id || unit.name}`}
                    type="button"
                    disabled={isUnavailable}
                    onClick={() => {
                      addToCart(unitPickerProduct, unit);
                      setUnitPickerProduct(null);
                    }}
                    style={{
                      width: '100%', textAlign: 'left',
                      border: `1px solid ${isUnavailable ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.08)'}`,
                      background: isUnavailable ? 'rgba(255,255,255,0.02)' : '#252A3D',
                      borderRadius: 14, padding: '14px 16px',
                      cursor: isUnavailable ? 'not-allowed' : 'pointer',
                      opacity: isUnavailable ? 0.45 : 1,
                      transition: 'all 150ms',
                    }}
                    onMouseEnter={(e) => { if (!isUnavailable) { e.currentTarget.style.borderColor = 'rgba(240,180,41,0.4)'; e.currentTarget.style.background = 'rgba(240,180,41,0.06)'; }}}
                    onMouseLeave={(e) => { if (!isUnavailable) { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.background = '#252A3D'; }}}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                      <div style={{ fontWeight: 800, color: '#FFFFFF', fontSize: '0.9rem', fontFamily: '"Cabinet Grotesk", sans-serif' }}>{unit.name}</div>
                      <div style={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 800, color: '#F0B429', fontSize: '0.92rem' }}>
                        {fmt(activeUnitPrice)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: '0.72rem', color: '#6B7A99' }}>
                      <span>
                        1 {String(unit.name).toLowerCase()} = {unit.conversionQty} {String(unitPickerProduct.baseUnitName || 'unit').toLowerCase()}{Number(unit.conversionQty) === 1 ? '' : 's'}
                      </span>
                      <span style={{ color: availableCount > 0 ? '#00C9B1' : '#FF6B6B' }}>{availableCount} available</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {showCustomerModal && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(7,10,18,0.75)',
            backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 220, padding: 16,
          }}
          onClick={(e) => { if (e.target === e.currentTarget && !customerSaving) resetCustomerModal(); }}
        >
          <div
            style={{
              width: '100%', maxWidth: 420,
              background: '#1E2235',
              borderRadius: 20,
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 32px 64px rgba(0,0,0,0.5)',
              padding: 24,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontFamily: '"Cabinet Grotesk", sans-serif', fontSize: '1.1rem', fontWeight: 800, color: '#FFFFFF' }}>Add Customer</div>
                <div style={{ fontSize: '0.76rem', color: '#6B7A99', marginTop: 4 }}>Save the buyer and keep checkout moving.</div>
              </div>
              <button
                type="button"
                onClick={resetCustomerModal}
                disabled={customerSaving}
                style={{
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: '#252A3D', color: '#6B7A99',
                  width: 32, height: 32, borderRadius: 10,
                  cursor: 'pointer', fontWeight: 800, flexShrink: 0,
                  transition: 'all 150ms',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#FF6B6B'; e.currentTarget.style.borderColor = 'rgba(255,107,107,0.3)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#6B7A99'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
              >
                ✕
              </button>
            </div>

            {customerError && (
              <div style={{
                marginBottom: 16, padding: '10px 12px', borderRadius: 10,
                background: 'rgba(255,107,107,0.1)', color: '#FF6B6B',
                fontSize: '0.76rem', fontWeight: 700,
                border: '1px solid rgba(255,107,107,0.2)',
              }}>
                {customerError}
              </div>
            )}

            <form onSubmit={handleCreateCustomer}>
              <div style={{ display: 'grid', gap: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: '#6B7A99', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Name *
                  </label>
                  <input
                    autoFocus
                    value={customerForm.name}
                    onChange={(e) => setCustomerForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Ama Mensah"
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: 10,
                      border: '1px solid rgba(255,255,255,0.08)',
                      fontSize: '0.88rem', fontFamily: '"Cabinet Grotesk", sans-serif',
                      outline: 'none', background: '#252A3D', color: '#E2E8F0',
                      boxSizing: 'border-box', transition: 'border-color 150ms',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#F0B429'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: '#6B7A99', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Phone
                    </label>
                    <input
                      value={customerForm.phone}
                      onChange={(e) => setCustomerForm((prev) => ({ ...prev, phone: e.target.value }))}
                      placeholder="024..."
                      style={{
                        width: '100%', padding: '10px 12px', borderRadius: 10,
                        border: '1px solid rgba(255,255,255,0.08)',
                        fontSize: '0.88rem', fontFamily: '"Cabinet Grotesk", sans-serif',
                        outline: 'none', background: '#252A3D', color: '#E2E8F0',
                        boxSizing: 'border-box', transition: 'border-color 150ms',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = '#F0B429'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: '#6B7A99', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      value={customerForm.email}
                      onChange={(e) => setCustomerForm((prev) => ({ ...prev, email: e.target.value }))}
                      placeholder="you@example.com"
                      style={{
                        width: '100%', padding: '10px 12px', borderRadius: 10,
                        border: '1px solid rgba(255,255,255,0.08)',
                        fontSize: '0.88rem', fontFamily: '"Cabinet Grotesk", sans-serif',
                        outline: 'none', background: '#252A3D', color: '#E2E8F0',
                        boxSizing: 'border-box', transition: 'border-color 150ms',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = '#F0B429'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: '#6B7A99', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Pricing tier
                  </label>
                  <select
                    value={customerForm.pricingTier}
                    onChange={(e) => setCustomerForm((prev) => ({ ...prev, pricingTier: e.target.value }))}
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: 10,
                      border: '1px solid rgba(255,255,255,0.08)',
                      fontSize: '0.88rem', fontFamily: '"Cabinet Grotesk", sans-serif',
                      outline: 'none', background: '#252A3D', color: '#E2E8F0',
                      boxSizing: 'border-box',
                    }}
                  >
                    {PRICING_TIER_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
                <button
                  type="button"
                  onClick={resetCustomerModal}
                  disabled={customerSaving}
                  style={{
                    padding: '9px 18px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)',
                    background: '#252A3D', color: '#6B7A99', cursor: 'pointer',
                    fontSize: '0.84rem', fontWeight: 700,
                    fontFamily: '"Cabinet Grotesk", sans-serif',
                    transition: 'all 150ms',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#E2E8F0'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#6B7A99'; }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={customerSaving}
                  style={{
                    padding: '9px 20px', borderRadius: 10, border: 'none',
                    background: '#F0B429', color: '#0B0E17', cursor: customerSaving ? 'default' : 'pointer',
                    fontSize: '0.84rem', fontWeight: 800,
                    fontFamily: '"Cabinet Grotesk", sans-serif',
                    opacity: customerSaving ? 0.7 : 1,
                    transition: 'all 150ms',
                  }}
                >
                  {customerSaving ? 'Saving...' : 'Save customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
